// helpers/blogApi.js
import axios from 'axios'
import { blogApiUrl } from '../utils/variables'

/**
 * Fetch all articles (blog posts) from custom API
 */
export const fetchBlogPosts = async (options = {}) => {
    try {
        const {
            page = 1,
            pageSize = 10,
            category,
        } = options

        const params = {
            page,
            page_size: pageSize,
        }

        // Add category parameter if provided
        if (category) {
            params.category = category
        }

        const response = await axios.get(`${blogApiUrl}/api/blogs/`, {
            params: params
        })

        // Custom API structure mapping
        // response.data is { total, page, page_size, pages, results: [...] }
        const data = response.data.results || response.data.data || (Array.isArray(response.data) ? response.data : [])
        
        return {
            data: data,
            meta: {
                pagination: {
                    page: response.data.page || page,
                    pageSize: response.data.page_size || pageSize,
                    total: response.data.total || response.data.count || 0
                }
            }
        }
    } catch (error) {
        console.error('Error fetching blogs:', error)
        throw error
    }
}

/**
 * Fetch a single article by ID
 */
export const fetchBlogPost = async (id) => {
    try {
        const response = await axios.get(`${blogApiUrl}/api/blogs/`, {
            params: {
                id
            }
        })

        // The user said GET /api/blogs/?id=<blog_id> returns full detail
        const data = response.data
        if (Array.isArray(data)) {
            if (data.length > 0) return data[0]
        } else if (data) {
            // Check if it's a wrapper like { status: 200, data: { ... } }
            return data.data || data
        }
        
        throw new Error('Blog not found')
    } catch (error) {
        console.error('Error fetching blog detail:', error)
        throw error
    }
}

/**
 * Fetch the latest/recent blog posts for the given post's id - powers the
 * blog detail screen's bottom "Latest Blogs" section (was previously a
 * client-side same-category filter over the full post list).
 */
export const fetchRecentBlogPosts = async (id) => {
    try {
        const response = await axios.get(`${blogApiUrl}/api/blogs/recent/`, {
            params: {
                id
            }
        })

        const data = response.data.results || response.data.data || (Array.isArray(response.data) ? response.data : [])
        return {
            data: data,
            meta: {}
        }
    } catch (error) {
        console.error('Error fetching recent blogs:', error)
        throw error
    }
}

/**
 * Fetch articles by category
 */
export const fetchBlogPostsByCategory = async (categorySlug) => {
    try {
        const response = await axios.get(`${blogApiUrl}/api/blogs/`, {
            params: {
                category: categorySlug
            }
        })

        const data = response.data.results || response.data.data || (Array.isArray(response.data) ? response.data : [])
        return {
            data: data,
            meta: {
                pagination: {
                    page: response.data.page || 1,
                    pageSize: response.data.page_size || 10,
                    total: response.data.total || response.data.count || data.length || 0
                }
            }
        }
    } catch (error) {
        console.error('Error fetching blogs by category:', error)
        throw error
    }
}

/**
 * Fetch all categories
 */
export const fetchCategories = async () => {
    try {
        const response = await axios.get(`${blogApiUrl}/api/blogs/categories/`)
        
        // Map the categories from the response
        // Assuming response.data is an array or has a results/data field
        const data = response.data.results || response.data.data || (Array.isArray(response.data) ? response.data : [])
        
        return {
            data: data.map(cat => ({
                id: cat.id,
                name: cat.name,
                slug: cat.slug,
                description: cat.description
            })),
            meta: {}
        }
    } catch (error) {
        console.error('Error fetching categories:', error)
        return {
            data: [],
            meta: {}
        }
    }
}
